import React, { useState, useEffect } from "react";
import { Grid, Typography, Box, useTheme, Button } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrivateLayout from "layouts/privateLayout";
import { useParams } from "react-router-dom";
import DateFormatter from "helper/dateFormartter";
import ProductTable from "./productTable";
import RequestTable from "./requestTable";
import TaxTable from "./taxTable";
import TaxType from "../../data/taxType.json";
import CountryCode from "../../data/countryCode.json";
import QRCode from 'qrcode';
import BasicBreadcrumbs from "components/breadcrumb";
import axios from "axios";
import { tokens } from "../../theme";
import Header from "components/header";
import { ReportPDF } from "./reportPdf";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { useApiClient } from "context/ApiClientContext";
import DateTimeFormatter from "helper/dateTimeFormartter";

const DetailItem = ({ label, value }) => (
  <Box display="flex" alignItems="top" mb={1}>
    <Typography
      variant="subtitle1"
      sx={{ minWidth: 200, fontWeight: "bold", }}
      width=""
    >
      {label}
    </Typography>
    <Typography mr={2}>:</Typography>
    {value ? <Typography
      variant="body1"
      sx={{
        overflowWrap: "break-word",
        wordBreak: "break-word",
      }}
    >
      {value ? ` ${value}` : " "}
    </Typography> : " "}
  </Box>
);

export default function DetailsInfo() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const apiClient = useApiClient();
  const { id } = useParams();
  const [data, setData] = useState({
    invoiceInfo: {},
    supplierInfo: {},
    productInfo: [],
    taxInfo: [],
    requestInfo: [],
    buyerInfo: {},
  });
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const dateFormatter = DateFormatter();
  const dateTimeFormatter = DateTimeFormatter();
  const companyId = localStorage.getItem('companyId')

  const getOne = async (id) => {
    try {
      const response = await axios.get(
        `${apiClient}/${companyId}/document/${id}?filters=pagination[page]=1`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: "application/json",
          },
        }
      );

      const response2 = await axios.get(
        `${apiClient}/transactionhistory?companyId=${companyId}&docId=${id}&pagenum=1&pagesize=100`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: "application/json",
          },
        }
      );

      const formatNumber = (value) => {
        return typeof value === 'number' ? value.toFixed(2) : '0.00';
      };

      const dataRows = response?.data;
      const transactionData = response2?.data;

      // Assuming dataRows.docDetails is an array
      const docDetails = dataRows.docDetails;

      const fortyYearsAgo = new Date();
      fortyYearsAgo.setFullYear(fortyYearsAgo.getFullYear() - 40);

      const isDateValid = (date) => {
        const parsedDate = new Date(date);
        return parsedDate >= fortyYearsAgo;
      };

      // Retrieve submissionuuid and docuuid from the first element of docDetails array
      const submissionuuid =
        docDetails.length > 0 ? docDetails[0].submissionuuid : "";
      const docuuid = docDetails.length > 0 ? docDetails[0].docuuid : "";

      const invoiceInfo = {
        docLink: dataRows.doclink,
        company: dataRows.companyId,
        docSource: dataRows.docSource,
        typeCode: dataRows.typeCode,
        type: dataRows.typeCode_Value,
        number: dataRows.docNum,
        oriRefNumber: dataRows.refDocNum,
        issuanceDate: dateFormatter.format(dataRows.issueDateTime),
        currencyCode: dataRows.currency,
        exchangeRate: dataRows.exchangeRate != null
          ? (Number(dataRows.exchangeRate).toFixed(6))
          : 0.000000,
        billFreq: dataRows.billFreq,
        // billPeriod: "",
        irbm: dataRows.eDocNum,
        longId: dataRows.longid,
        submissionuuid: submissionuuid,
        docuuid: docuuid,
        dueDate: dateFormatter.format(dataRows.docDueDate),
        version: (dataRows.version),
        signature: dataRows.signature,
        date: dateFormatter.format(dataRows.docDate),
        submitdate: isDateValid(dataRows.submitdate) ? dateTimeFormatter.format(dataRows.submitdate) : null,
        validatedate: isDateValid(dataRows.validatedate) ? dateTimeFormatter.format(dataRows.validatedate) : null,
        payMode: dataRows.payMode,
        payMode_Value: dataRows.payMode_Value,
        payTerm: dataRows.payTerm,
        depositAmt: formatNumber(dataRows.depositAmt),
        depositDateTime: isDateValid(dataRows.depositDateTime) ? dateFormatter.format(dataRows.depositDateTime) : null,
        depositNum: dataRows.depositNum,
        depositRefNum: dataRows.depositRefNum,
        total_ExcludeTax: formatNumber(dataRows.total_ExcludeTax),
        total_IncludeTax: formatNumber(dataRows.total_IncludeTax),
        total_PayableAmt: formatNumber(dataRows.total_PayableAmt),
        total_NettAmt: formatNumber(dataRows.total_NettAmt),
        total_DiscAmt: formatNumber(dataRows.total_DiscAmt),
        total_ChargeAmt: formatNumber(dataRows.total_ChargeAmt),
        total_TaxAmt: formatNumber(dataRows.total_TaxAmt),
        roundingAmt: formatNumber(dataRows.roundingAmt),
        total_TaxableAmt: formatNumber(dataRows.total_TaxableAmt),
        total_TaxTypeAmt: formatNumber(dataRows.total_TaxTypeAmt),
        taxType: dataRows.taxType,
        taxType_Value: dataRows.taxType_Value,
        status: dataRows.status,
        remark: dataRows.remark,
        addedBy: dataRows.addedBy,
        addedDate: dateFormatter.format(dataRows.addedDate),
        updatedBy: dataRows.updatedBy,
        updatedDate: dateFormatter.format(dataRows.updatedDate),
        importNum: dataRows.importNum,
        incoterms: dataRows.incoterms,
        ftaInfo: dataRows.ftaInfo,
        exporterAcctId: dataRows.exporterAcctId,
        exportNum: dataRows.exportNum,
      };

      const supplierInfo = {
        id: dataRows.supID,
        name: !dataRows.supplier_Name ? 'NA' : dataRows.supplier_Name,
        email: !dataRows.supplier_Email ? 'NA' : dataRows.supplier_Email ,
        phone: !dataRows.supplier_Contact ? 'NA' : dataRows.supplier_Contact,
        msicNum: !dataRows.supplier_MSIC ? 'NA' : dataRows.supplier_MSIC,
        accNum: !dataRows.supBankAcctNum ? 'NA' : dataRows.supBankAcctNum,
        tin: !dataRows.supplier_Tin ? 'NA' : dataRows.supplier_Tin,
        sstNum: !dataRows.supplier_SST ? 'NA' : dataRows.supplier_SST,
        tourismTaxNum: !dataRows.supplier_TourismTax ? 'NA' : dataRows.supplier_TourismTax,
        address:
          dataRows.supplier_Addr0 +
          " " +
          dataRows.supplier_Addr1 +
          " " +
          dataRows.supplier_Addr2 +
          " " +
          dataRows.supplier_Zipcode +
          " " +
          dataRows.supplier_City,
        state: dataRows.supplier_State,
        country: dataRows.supplier_Country,
        bussinessDesc: dataRows.supplier_BizNature,
        bussinessType: dataRows.supplier_BizType,
        bussinessValue:  (dataRows.supplier_BizValue == 'NA' || !dataRows.supplier_BizValue || dataRows.supplier_BizValue === "" || dataRows.supplier_BizValue === null || dataRows.supplier_BizValue === undefined ) ? 'NA' : dataRows.supplier_BizValue,
      };

      const buyerInfo = {
        id: dataRows.buyID,
        name: !dataRows.buyer_Name ? 'NA' : dataRows.buyer_Name,
        email: !dataRows.buyer_Email ? 'NA' : dataRows.buyer_Email,
        phone: !dataRows.buyer_Contact ? 'NA' : dataRows.buyer_Contact,
        tin: !dataRows.buyer_Tin ? 'NA' : dataRows.buyer_Tin,
        sst: !dataRows.buyer_SST ? 'NA' : dataRows.buyer_SST,
        address:
          dataRows.buyer_Addr0 +
          " " +
          dataRows.buyer_Addr1 +
          " " +
          dataRows.buyer_Addr2 +
          " " +
          dataRows.buyer_Zipcode +
          " " +
          dataRows.buyer_City,
        state: dataRows.buyer_State,
        country: dataRows.buyer_Country,
        bussinessType: dataRows.buyer_BizType,
        bussinessValue: (dataRows.buyer_BizValue == 'NA' || !dataRows.buyer_BizValue || dataRows.buyer_BizValue === "" || dataRows.buyer_BizValue === null || dataRows.buyer_BizValue === undefined ) ? 'NA' : dataRows.buyer_BizValue,
      };

      const productInfo = dataRows.docDetails;
      for (let i = 0; i < productInfo.length; i++) {
        let data = productInfo[i];
        mappingValue(data, "taxTypeId", TaxType, "taxType");
        mappingValue(data, "originCountry", CountryCode, "originCountry");
      }

      const requestInfo = [];

      const taxInfo = dataRows.docTaxes;
      for (let i = 0; i < taxInfo.length; i++) {
        let data = taxInfo[i];
      }

      // const taxInfo = [];
      // {
      //   docid: dataRows.docid,
      //   lineId: dataRows.lineId,
      //   taxCode: dataRows.taxCode,
      //   taxValue: dataRows.taxValue,
      //   totalTaxAmt: dataRows.totalTaxAmt
      // };

      qrCodeGenerate(invoiceInfo);

      setData({
        invoiceInfo,
        supplierInfo,
        productInfo,
        taxInfo,
        requestInfo,
        buyerInfo,
      });
      return { dataRows, transactionData };
    } catch (error) {
      console.error("An error occurred:", error.response);
    }
  };

  function mappingValue(data, field, mapping, newField) {
    if (field in data) {
      const dataField = data[field];
      const matchingDescription = mapping.find((item) => {
        if (mapping === "TaxType") {
          if (item.Code === 0 + dataField) {
            return item;
          }
        } else {
          if (item.Code === dataField) {
            return item;
          }
        }
      });

      if (matchingDescription) {
        const desc = matchingDescription.Description;
        data[newField] = desc;
      }
    }
  }
  const structuredList = (transactionDataArray) => {
    // Check if transactionDataArray is an array
    if (Array.isArray(transactionDataArray)) {
      // Proceed with mapping if it's an array
      let requestInfo = transactionDataArray.map((dataRows) => ({
        transactionId: dataRows.transactionId,
        tran_DateTime: dateTimeFormatter.format(dataRows.tran_DateTime),
        method: dataRows.method,
        url: dataRows.url,
        json: dataRows.json,
        status_Code: dataRows.status_Code,
        status_message: dataRows.status_message,
        response_message: dataRows.response_message,
        batch_id: dataRows.batch_id,
      }));

      setData(prevData => ({
        ...prevData,
        requestInfo,
      }));
    } else if (transactionDataArray === "No Data.") {
      // Handle the case where "No Data." is returned
      setData(prevData => ({
        ...prevData,
        requestInfo: [], // Set requestInfo to an empty array
      }));
    } else {
      // Handle unexpected cases
      console.error("Unexpected data format:", transactionDataArray);
    }
  };

  // const taxList = async (data) => {
  //   const dataRows = data.row;

  //   let taxInfo = [];
  //   if (dataRows.tax) {
  //     taxInfo = [
  //       {
  //         docid: dataRows.docid,
  //         lineId: dataRows.lineId,
  //         taxCode: dataRows.taxCode,
  //         taxValue: dataRows.taxValue,
  //         totalTaxAmt: dataRows.totalTaxAmt
  //       },
  //     ];
  //   } else {
  //     taxInfo = [];
  //   }

  //   setData({
  //     taxInfo,
  //   });
  // };

  const qrCodeGenerate = async (invoiceInfo) => {
    const url = `${invoiceInfo.docLink}`;
    // const url = `https://preprod.myinvois.hasil.gov.my/${invoiceInfo.docuuid}/share/${invoiceInfo.longId}`;

    if (url) {
      QRCode.toDataURL(url, { errorCorrectionLevel: 'H' }, (err, url) => {
        if (err) throw err;
        setQrCodeDataUrl(url);
      });
    }

  }
  // useEffect(() => {
  //   getOne(id);
  //   // structredList(dataRows);
  // }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      const { dataRows, transactionData } = await getOne(id);
      structuredList(transactionData);
      // taxList(dataRows);
    };

    fetchData();
  }, [id]);

  return (
    <PrivateLayout>
      <BasicBreadcrumbs first="Receivable" second="Details" />
      <Box display="flex" justifyContent="space-between">
        <Header title="Details Page" />
        <Box display="flex" justifyItems="center">
          {/* {(data.invoiceInfo.longId  === 'null' || data.invoiceInfo.longId  === '' || !data.invoiceInfo.longId) ? 'true' : 'false'}
          <PDFDownloadLink document={<ReportPDF data={data} qrDataUrl={qrCodeDataUrl} />} fileName={`invoice ${data.invoiceInfo.number}`}>
            <Button disabled= {!(data.invoiceInfo.longId  === 'null' || data.invoiceInfo.longId  === '' || !data.invoiceInfo.longId) ? 'true' : 'false'} variant="contained" size="small">Download Invoice</Button>
            {({ blob, url, loading, error }) => (loading ? '' : <Button variant="contained" size="small">Download Invoice</Button>)}
          </PDFDownloadLink> */}
          <PDFDownloadLink
            document={<ReportPDF data={data} qrDataUrl={qrCodeDataUrl} />}
            fileName={`invoice ${data.invoiceInfo.number}`}
          >
            {({ blob, url, loading, error }) => (
              !data.invoiceInfo.longId || data.invoiceInfo.longId === 'null' || data.invoiceInfo.longId === '' ? null :
              <Button
                variant="contained"
                size="small"
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Download Invoice'}
              </Button>
            )}
          </PDFDownloadLink>
        </Box>
      </Box>

      <Accordion defaultExpanded mt={5}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          sx={{
            backgroundColor: `${colors.grey[700]}`,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            textTransform="uppercase"
            sx={{ textDecoration: "underline" }}
          >
            Invoice Details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} py={2}>
            <Grid item xs={12} display="flex" justifyContent="space-between">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DetailItem
                    label="Invoice Number"
                    value={data.invoiceInfo.number}
                  />
                  <DetailItem
                    label="Invoice Type"
                    value={
                      data.invoiceInfo.typeCode + " " + data.invoiceInfo.type
                    }
                  />
                  <DetailItem
                    label="Invoice Ref. Number"
                    value={data.invoiceInfo.oriRefNumber}
                  />
                  <DetailItem
                    label="Issuance Date"
                    value={data.invoiceInfo.issuanceDate}
                  />
                  <DetailItem
                    label="Invoice Date"
                    value={data.invoiceInfo.date}
                  />
                  <DetailItem
                    label="Invoice Due Date"
                    value={data.invoiceInfo.dueDate}
                  />
                  <DetailItem
                    label="IRB Date"
                    value={data.invoiceInfo.submitdate}
                  />
                  <DetailItem
                    label="Validated Date"
                    value={data.invoiceInfo.validatedate}
                  />
                  <DetailItem
                    label="Added Date"
                    value={data.invoiceInfo.addedDate}
                  />
                  <DetailItem
                    label="Currency Code"
                    value={data.invoiceInfo.currencyCode}
                  />
                  <DetailItem
                    label="Currency Exchange Rate"
                    value={data.invoiceInfo.exchangeRate}
                  />
                  <DetailItem
                    label="Frequency of Billing"
                    value={data.invoiceInfo.billFreq}
                  />
                  {/* <DetailItem
                    label="Billing Period"
                    value={data.invoiceInfo.billPeriod}
                  /> */}
                  <DetailItem
                    label="Invoice Version"
                    value={data.invoiceInfo.version}
                  />
                  {/* <DetailItem
                    label="Tax Type"
                    value={
                      data.invoiceInfo.taxType +
                      " " +
                      data.invoiceInfo.taxType_Value
                    }
                  /> */}
                  <DetailItem
                    label="Total Exclude Tax"
                    value={data.invoiceInfo.total_ExcludeTax}
                  />{" "}
                  <DetailItem
                    label="Total Include Tax"
                    value={data.invoiceInfo.total_IncludeTax}
                  />
                  <DetailItem
                    label="Total Payable Amount"
                    value={data.invoiceInfo.total_PayableAmt}
                  />
                  <DetailItem
                    label="Total Net Amount"
                    value={data.invoiceInfo.total_NettAmt}
                  />
                  <DetailItem
                    label="Total Discount Amount"
                    value={data.invoiceInfo.total_DiscAmt}
                  />
                  <DetailItem
                    label="Total Charge Amount"
                    value={data.invoiceInfo.total_ChargeAmt}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <DetailItem
                    label="Total Tax Amount"
                    value={data.invoiceInfo.total_TaxAmt}
                  />
                  <DetailItem
                    label="Total Rounding Amount"
                    value={data.invoiceInfo.roundingAmt}
                  />
                  {/* <DetailItem
                    label="Total Tax Type Amount"
                    value={data.invoiceInfo.total_TaxTypeAmt}
                  /> */}
                  <DetailItem
                    label="Long Id"
                    value={data.invoiceInfo.longId}
                  />
                  {/* <DetailItem
                    label="IRB Unique Id"
                    value={data.invoiceInfo.irbm}
                  /> */}
                  <DetailItem
                    label="Submission UUID"
                    value={data.invoiceInfo.submissionuuid}
                    sx={{ wordBreak: "break-word", }}
                  />
                  <DetailItem
                    label="Document UUID"
                    value={data.invoiceInfo.docuuid}
                    sx={{ wordBreak: "break-word", }}
                  />
                  <DetailItem
                    label="Payment Mode"
                    value={
                      data.invoiceInfo.payMode +
                      " " +
                      data.invoiceInfo.payMode_Value
                    }
                  />
                  <DetailItem
                    label="Payment Term"
                    value={data.invoiceInfo.payTerm}
                  />
                  <DetailItem
                    label="Deposit Amount"
                    value={data.invoiceInfo.depositAmt}
                  />
                  <DetailItem
                    label="Deposit Date"
                    value={data.invoiceInfo.depositDateTime}
                  />{" "}
                  <DetailItem
                    label="Deposit Number"
                    value={data.invoiceInfo.depositNum}
                  />{" "}
                  <DetailItem
                    label="Deposit Ref Number"
                    value={data.invoiceInfo.depositRefNum}
                  />{" "}
                  <DetailItem label="Import Number" value={data.invoiceInfo.importNum} />
                  <DetailItem label="Incoterms" value={data.invoiceInfo.incoterms} />
                  <DetailItem label="FTA Info" value={data.invoiceInfo.ftaInfo} />
                  <DetailItem label="Exporter Account Id" value={data.invoiceInfo.exporterAcctId} />
                  <DetailItem label="Export Number" value={data.invoiceInfo.exportNum} />

                  <DetailItem label="Status" value={data.invoiceInfo.status} />
                  <DetailItem label="Remark" value={data.invoiceInfo.remark} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel2-content"
          id="panel2-header"
          sx={{
            backgroundColor: `${colors.grey[700]}`,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            textTransform="uppercase"
            sx={{ textDecoration: "underline" }}
          >
            Supplier & Buyer Details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2} py={2}>
            <Grid
              item
              mt={2}
              xs={12}
              md={6}
              display="flex"
              justifyContent="space-between"
            >
              {/* Suplier */}
              <Grid container spacing={2}>
                {/* <Typography fontWeight="fontWeightMedium" variant="h6" mb={2}>
                  <Divider textAlign="center">
                    <Chip label="Supplier" />
                  </Divider>
                </Typography> */}

                <Grid item xs={12}>
                  <DetailItem
                    label="Supplier Name"
                    value={data.supplierInfo.name}
                  />
                  <DetailItem
                    label="Supplier Email"
                    value={data.supplierInfo.email}
                  />
                  <DetailItem
                    label="Supplier Phone Number"
                    value={data.supplierInfo.phone}
                  />
                  {/* <DetailItem
                    label="Supplier Account Number"
                    value={data.supplierInfo.accNum}
                  /> */}
                  <DetailItem
                    label="Supplier Tax Identification Number"
                    value={data.supplierInfo.tin}
                  />
                  <DetailItem
                    label="Supplier SST Number"
                    value={data.supplierInfo.sstNum}
                  />
                  <DetailItem
                    label=" Supplier Tourism Tax Number"
                    value={data.supplierInfo.tourismTaxNum}
                  />
                  <DetailItem
                    label="Supplier MSIC Number"
                    value={data.supplierInfo.msicNum}
                  />
                  <DetailItem
                    label="Supplier Registration Number"
                    value={data.supplierInfo.bussinessValue}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Buyer */}

            <Grid
              item
              mt={2}
              xs={12}
              md={6}
              display="flex"
              justifyContent="space-between"
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <DetailItem label="Buyer Name" value={data.buyerInfo.name} />
                  <DetailItem
                    label="Buyer Email"
                    value={data.buyerInfo.email}
                  />
                  <DetailItem
                    label="Buyer Phone Number"
                    value={data.buyerInfo.phone}
                  />

                  <DetailItem
                    label="Buyer Tax Identification Number"
                    value={data.buyerInfo.tin}
                  />
                  <DetailItem
                    label="Buyer SST Number"
                    value={data.buyerInfo.sst}
                  />
                  <DetailItem
                    label="Buyer Registration Number"
                    value={data.buyerInfo.bussinessValue}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel3-content"
          id="panel3-header"
          sx={{
            backgroundColor: `${colors.grey[700]}`,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            textTransform="uppercase"
            sx={{ textDecoration: "underline" }}
          >
            Products Details
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ProductTable rows={data.productInfo} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel3-content"
          id="panel3-header"
          sx={{
            backgroundColor: `${colors.grey[700]}`
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
            textTransform="uppercase"
            sx={{ textDecoration: "underline" }}
          >
            Total Tax
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TaxTable rows={data.taxInfo} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel4-content"
          id="panel4-header"
          sx={{
            backgroundColor: `${colors.grey[700]}`
          }}
        >
          <Typography
            variant="h6"
            textTransform="uppercase"
            fontWeight="bold"
            sx={{ textDecoration: "underline" }}
          >
            Transaction History
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RequestTable rows={data.requestInfo} />
        </AccordionDetails>
      </Accordion>
    </PrivateLayout>
  );
}